import { useEffect, useRef, useState } from 'react';

import { AgentDelegateFollowUp } from '@components/Agents/AgentDelegateFollowUp/AgentDelegateFollowUp';
import { AgentReplay } from '@components/Agents/AgentReplay/AgentReplay';
import { AgentTemplateConfig } from '@components/Agents/AgentTemplate/AgentTemplateConfig';
import { AgentTemplatePreview } from '@components/Agents/AgentTemplate/AgentTemplatePreview';
import { AgentTemplates } from '@components/Agents/AgentTemplate/AgentTemplates';
import palette from '@lib/core/src/theme/palette';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import { MeetingTemplateDto, UserAgentsConfigDto } from '@/api/generated';
import { useUserAgents } from '@/hooks/useUserAgents';

import { AgentConfigOverview } from './AgentConfigOverview';
import { AgentConfigContainer, DetailsContainer, DetailsHeader } from './AgentConfigStyles';
import { AgentProcess } from './AgentProcess';
import { AgentConfigType } from './AgentsListConfig';

export const AgentConfig = ({
  onBack,
  selectedAgent
}: {
  onBack: () => void;
  selectedAgent: AgentConfigType;
}) => {
  const { data: agents } = useUserAgents();

  const [isActive, setIsActive] = useState(false);
  const [selectedTemplate, setSelectTemplate] = useState<MeetingTemplateDto | null>(null);
  const isPredefinedTemplateRef = useRef<boolean>(false);

  useEffect(() => {
    if (!agents?.items?.length || !selectedAgent) return;
    const userAgentDetails = agents.items[0];
    const agentDetailsKey = selectedAgent.activeKey as keyof Omit<
      UserAgentsConfigDto,
      'createdAt' | 'updatedAt'
    >;
    setIsActive(userAgentDetails?.[agentDetailsKey]?.enabled ?? false);
  }, [agents, selectedAgent]);

  const renderAgentConfigContent = () => {
    switch (selectedAgent.agentId) {
      case 0:
        return (
          <AgentTemplates
            setSelectTemplate={setSelectTemplate}
            isPredefinedTemplateRef={isPredefinedTemplateRef}
          />
        );
      case 1:
        return <AgentReplay />;
      case 2:
        return <AgentDelegateFollowUp />;
      default:
        return <AgentReplay />;
    }
  };

  return (
    <AgentConfigContainer>
      {selectedTemplate ? (
        <>
          <AgentTemplateConfig
            template={selectedTemplate}
            isPredefinedTemplateRef={isPredefinedTemplateRef}
            setSelectTemplate={setSelectTemplate}
          />
          <AgentTemplatePreview sections={selectedTemplate.sections || []} />
        </>
      ) : (
        <>
          <DetailsContainer
            sx={{
              paddingRight: {
                md: 2,
                lg: 8
              },
              gap: { md: 2, lg: 4 }
            }}
          >
            <DetailsHeader>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  sx={{
                    padding: '8px',
                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                  }}
                  onClick={onBack}
                >
                  <ArrowBackIosIcon fontSize="small" />
                  <Typography sx={{ color: palette.grey[500] }}>Back</Typography>
                </IconButton>
              </Box>
            </DetailsHeader>
            <Stack
              sx={{
                height: { md: 'calc(100% - 56px)', lg: 'calc(100% - 112px)' },
                overflowY: 'auto'
              }}
            >
              <AgentConfigOverview
                name={selectedAgent.agentName}
                description={selectedAgent.agentDescription}
                dataSources={selectedAgent.dataSources}
              />
              {renderAgentConfigContent()}
            </Stack>
          </DetailsContainer>
          <AgentProcess process={selectedAgent.agentProcess} />
        </>
      )}
    </AgentConfigContainer>
  );
};
